import type { QuizPersistenceIdentity } from './quizPersistenceIdentity';
import { sessionRequests } from './sessionRequests';

const identityWriteTails = new Map<string, Promise<void>>();
const IDENTITY_LOCK_RETRY_DELAYS_MS = [100, 250] as const;

/** Identifies the database's fail-fast anonymous lifecycle lock outcome. */
export function isAnonymousIdentityLockConflict(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const fields = error as Record<string, unknown>;
  return fields.code === '40001' && fields.message === 'anonymous_identity_lock_conflict';
}

/**
 * Prevents this browser runtime from racing two database writes that lock the
 * same player identity. Postgres remains the cross-runtime concurrency owner.
 * Opt-in retries cover only the known rolled-back anonymous lock conflict.
 * Guard before dispatch; preserve an already-dispatched write's acknowledgment.
 */
export async function runSerializedSelfIdentityWrite<T>(
  identity: QuizPersistenceIdentity,
  operation: () => Promise<T>,
  options?: { retryLockConflicts?: boolean }
): Promise<T> {
  const retryLockConflicts = options?.retryLockConflicts === true;
  const assertOwner = retryLockConflicts ? sessionRequests.captureOwner() : null;
  const key = identity.userId;
  const previous = identityWriteTails.get(key) ?? Promise.resolve();
  const current = previous
    .catch(() => undefined)
    .then(async () => {
      for (let retryIndex = 0; ; retryIndex += 1) {
        assertOwner?.();
        try {
          return await operation();
        } catch (error) {
          const retryDelay = IDENTITY_LOCK_RETRY_DELAYS_MS[retryIndex];
          if (
            !retryLockConflicts ||
            !isAnonymousIdentityLockConflict(error) ||
            retryDelay === undefined
          ) {
            throw error;
          }
          await new Promise<void>((resolve) => setTimeout(resolve, retryDelay));
        }
      }
    });
  const tail = current.then(
    () => undefined,
    () => undefined
  );
  identityWriteTails.set(key, tail);

  try {
    return await current;
  } finally {
    if (identityWriteTails.get(key) === tail) identityWriteTails.delete(key);
  }
}
